import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parts = [
  {
    label: "Prefazione",
    title: "Il motto",
    page: "10",
    chapters: [],
  },
  {
    label: "Parte I — La Rotta",
    title: "Dalla libertà al metodo",
    chapters: [
      { num: "1", title: "La mia storia", page: "13" },
      { num: "2", title: "Il metodo dietro il cambiamento", page: "26" },
      { num: "3", title: "La vendita come scuola di vita", page: "34" },
      { num: "4", title: "Costruire reti, non dipendere dai singoli", page: "42" },
      { num: "5", title: "Quando il digitale diventa un rischio", page: "50" },
      { num: "6", title: "La cybersicurezza non è solo tecnologia", page: "56" },
      { num: "7", title: "Metodo, digitale e sicurezza: l'unione", page: "63" },
    ],
  },
  {
    label: "Parte II — Il Metodo",
    title: "Struttura, leadership e continuità",
    chapters: [
      { num: "8", title: "Il linguaggio e i valori del metodo di mare", page: "73" },
      { num: "9", title: "Obiettivo strategico, missione e visione", page: "87" },
      { num: "10", title: "Il prodotto di ruolo", page: "95" },
      { num: "11", title: "Il metodo operativo", page: "104" },
      { num: "12", title: "Leadership, duplicazione, persone e affiancamento", page: "113" },
      { num: "13", title: "La disciplina del metodo", page: "120" },
      { num: "14", title: "Diventare professionisti nel mondo digitale", page: "128" },
      { num: "15", title: "Chiusura del manuale", page: "144" },
    ],
  },
  {
    label: "Appendici",
    title: "",
    chapters: [
      { num: "—", title: "Conclusioni", page: "150" },
      { num: "—", title: "Ringraziamenti", page: "154" },
      { num: "—", title: "Sponsor", page: "157" },
    ],
  },
];

export default function TableOfContents() {
  const [loading, setLoading] = useState(false);

  const downloadExtract = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/extract/first-chapter`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Metodo-di-Mare-Estratto-Capitolo-1.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Estratto scaricato. Buona lettura.");
    } catch (err) {
      toast.error("Errore nel download. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="toc"
      data-testid={TID.tocSection}
      className="py-24 md:py-32 border-t border-[#E5E1DA]"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="text-center mb-16">
          <p className="overline">Indice del libro</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
            Quindici capitoli,
            <br />
            <em className="text-[#0E7C8F]">una sola rotta.</em>
          </h2>
        </div>

        <div className="space-y-12">
          {parts.map((part) => (
            <div key={part.label}>
              <div className="flex items-baseline justify-between border-b border-[#0B192C] pb-3 mb-4">
                <p className="overline">{part.label}</p>
                {part.title && (
                  <p className="font-serif-display italic text-base md:text-lg text-[#3A4F6B] hidden sm:block">
                    {part.title}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                {part.chapters.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="flex items-baseline gap-6 py-4 border-b border-[#E5E1DA]"
                  >
                    <span className="font-serif-display text-base text-[#D4B895] w-10">—</span>
                    <span className="font-serif-display text-lg md:text-xl text-[#0B192C] flex-shrink-0">
                      {part.title}
                    </span>
                    <span className="flex-1 mx-3 h-3 dotted-leader self-end mb-1" />
                    <span className="font-sans-body text-xs tracking-widest text-[#3A4F6B]">
                      p. 10
                    </span>
                  </motion.div>
                )}
                {part.chapters.map((c, i) => (
                  <motion.div
                    key={`${part.label}-${c.num}-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.03 }}
                    className="flex items-baseline gap-6 py-4 border-b border-[#E5E1DA] group hover:border-[#0B192C] transition-colors"
                    data-testid={`toc-chapter-${part.label}-${c.num}`}
                  >
                    <span className="font-serif-display text-base text-[#E89B6C] w-10 flex-shrink-0">
                      {c.num}
                    </span>
                    <span className="font-serif-display text-lg md:text-xl text-[#0B192C]">
                      {c.title}
                    </span>
                    <span className="flex-1 mx-3 h-3 dotted-leader self-end mb-1" />
                    <span className="font-sans-body text-xs tracking-widest text-[#3A4F6B] flex-shrink-0">
                      p. {c.page}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Free chapter download CTA */}
        <div className="mt-16 border border-[#0B192C] p-8 md:p-12 bg-[#FFFFFF]">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <p className="overline">Estratto gratuito</p>
              <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
                Scarica il <em className="text-[#0E7C8F]">Capitolo 1</em> in PDF.
              </h3>
              <p className="mt-4 text-base font-light leading-relaxed text-[#3A4F6B] max-w-xl">
                "La mia storia" — il primo capitolo del libro, gratis e senza
                iscrizione. Per assaggiare il metodo prima di acquistarlo.
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <button
                onClick={downloadExtract}
                disabled={loading}
                data-testid="toc-download-extract-btn"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#0B192C] text-[#F9F8F6] text-sm uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-all disabled:opacity-60 w-full md:w-auto justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Preparazione...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Estratto del libro
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
